(() => {
  const data = globalThis.RicochetSearchData || { aliases: {}, vocabulary: [] };
  const aliases = data.aliases || {};
  const vocabulary = new Set(data.vocabulary || []);
  const aliasEntries = Object.entries(aliases);
  const cache = new Map();

  const normalizeWhitespace = (value) => value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

  const compact = (value) => normalizeWhitespace(value).toLowerCase().replace(/\s/g, '');

  const digitSignature = (value) => (String(value).match(/\d+/g) || []).join('|');

  const skus = new Set(data.skus || []);
  const stripModelYears = (value) => {
    const original = String(value || '');
    if (original.length > 160 || skus.has(compact(original))) return original;
    // Years must be separate tokens, not part of a model code, SKU or larger number.
    const year = '(?:19|20)\\d{2}';
    const boundary = '(?=$|[^a-z0-9])';
    const without = original
      .replace(new RegExp(`(^|[^a-z0-9])${year}\\s*(?:[-–—/]|to|through)\\s*${year}${boundary}`, 'gi'), '$1')
      .replace(new RegExp(`(^|[^a-z0-9])${year}${boundary}`, 'gi'), '$1');
    if (without === original || !/[a-z0-9]/i.test(without)) return original;
    return without.replace(/\s+/g, ' ').trim();
  };
  const atoms = (value) => normalizeWhitespace(value).toLowerCase().match(/[a-z]+|\d+/g) || [];
  // Inflect only the final noun of known product phrases, never arbitrary query words.
  const productForms = new Map();
  const productCache = new Map();
  const exactProductForms = new Map();
  const productGroups = new Map();
  let productWordLimit = 1;
  for (const label of data.productTypes || []) {
    const words = normalizeWhitespace(label).toLowerCase().split(' ');
    const noun = words.pop();
    if (!noun || !/^[a-z]+$/.test(noun)) continue;
    const singular = /ies$/.test(noun) ? `${noun.slice(0, -3)}y`
      : /(?:ches|shes|xes|zes|sses)$/.test(noun) ? noun.slice(0, -2)
        : /s$/.test(noun) && !/(?:ss|us|is)$/.test(noun) ? noun.slice(0, -1) : noun;
    const plural = /[^aeiou]y$/.test(singular) ? `${singular.slice(0, -1)}ies`
      : /(?:s|x|z|ch|sh)$/.test(singular) ? `${singular}es` : `${singular}s`;
    productWordLimit = Math.max(productWordLimit, words.length + 1);
    const groupKey = compact([...words, singular].join(' '));
    if (!productGroups.has(groupKey)) productGroups.set(groupKey, { label, singular: false, exact: [], forms: new Set() });
    const group = productGroups.get(groupKey);
    if (noun === singular && !group.singular) { group.label = label; group.singular = true; }
    group.exact.push(compact(label));
    for (const form of new Set([noun, singular, /(?:us|is)$/.test(singular) ? singular : plural])) {
      group.forms.add(compact([...words, form].join(' ')));
    }
  }
  for (const group of productGroups.values()) {
    group.exact.forEach((key) => exactProductForms.set(key, group.label));
    for (const key of group.forms) {
      if (!productForms.has(key)) productForms.set(key, group.label);
      else if (productForms.get(key) !== group.label) productForms.set(key, null);
    }
  }
  const resolveProductTypes = (value) => {
    if (skus.has(compact(value))) return value;
    const words = value.split(' ');
    const resolved = [];
    for (let start = 0; start < words.length;) {
      let label;
      let end = Math.min(words.length, start + productWordLimit);
      for (; end > start; end -= 1) {
        const key = compact(words.slice(start, end).join(' '));
        label = exactProductForms.get(key) || productForms.get(key);
        if (label) break;
      }
      resolved.push(label || words[start]);
      start = label ? end : start + 1;
    }
    return resolved.join(' ');
  };
  const phraseBuckets = new Map();
  for (const label of [...(data.phrases || []), ...Object.values(aliases), ...vocabulary]) {
    const key = compact(label);
    const parts = atoms(label);
    // Bound the permutation search; long catalog descriptions are not machine names.
    if (parts.length > 8 || key.length < 3 || !/[a-z]/.test(key)) continue;
    if (!phraseBuckets.has(key.length)) phraseBuckets.set(key.length, new Map());
    if (!phraseBuckets.get(key.length).has(key)) phraseBuckets.get(key.length).set(key, { label, parts });
  }

  // Consume complete catalog tokens in any order. Removing spaces first allows
  // "yamaha700kodiak" and "CRF 250 R X" without splitting model suffix identities.
  const consumesParts = (value, parts) => {
    if (!parts.length) return !value.length;
    return parts.some((part, index) => value.startsWith(part)
      && consumesParts(value.slice(part.length), parts.filter((_, offset) => offset !== index)));
  };
  const resolvePhrase = (value) => {
    const key = compact(value);
    const bucket = phraseBuckets.get(key.length);
    if (!bucket) return null;
    if (bucket.has(key)) return bucket.get(key).label;
    const matches = [...bucket.values()].filter(({ parts }) => consumesParts(key, parts));
    return matches.length === 1 ? matches[0].label : null;
  };

  const smallNumbers = 'zero one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen seventeen eighteen nineteen'.split(' ');
  const tens = { twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90 };
  const numberValue = (word) => smallNumbers.includes(word) ? smallNumbers.indexOf(word) : tens[word];
  const numberWords = (value) => value.toLowerCase().replace(
    /\b(?:zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)(?:\s+(?:hundred|thousand|and|zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety))*\b/g,
    (phrase) => {
      const words = phrase.split(/\s+/);
      // Accept conventional cardinals, not arbitrary sequences like "one two".
      const unit = '(?:one|two|three|four|five|six|seven|eight|nine)';
      const underHundred = `(?:${unit}|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|(?:twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)(?: ${unit})?)`;
      const underThousand = `(?:${unit} hundred(?: (?:and )?${underHundred})?|${underHundred})`;
      if (!new RegExp(`^(?:zero|${underThousand}|${unit} thousand(?: (?:and )?${underThousand})?)$`).test(phrase)) return phrase;
      let total = 0;
      let group = 0;
      for (const word of words) {
        if (word === 'and') continue;
        if (word === 'hundred') group *= 100;
        else if (word === 'thousand') { total += group * 1000; group = 0; }
        else group += numberValue(word);
      }
      return String(total + group);
    }
  );

  const resolvePhrases = (value) => {
    const whole = resolvePhrase(numberWords(value));
    if (whole) return whole;
    const tokens = value.split(' ');
    const result = [];
    for (let start = 0; start < tokens.length;) {
      let label = null;
      let end = Math.min(tokens.length, start + 10);
      for (; end > start; end -= 1) {
        label = resolvePhrase(numberWords(tokens.slice(start, end).join(' ')));
        if (label) break;
      }
      result.push(label || tokens[start]);
      start = label ? end : start + 1;
    }
    return result.join(' ');
  };

  // Customers commonly reverse a trim token and model number (for example,
  // "RZR 1000 XP" instead of the catalog's "RZR XP 1000"). Build only
  // unambiguous, adjacent number/word swaps from the catalog vocabulary.
  const reorderedAliasCandidates = new Map();
  aliasEntries.forEach(([, label]) => {
    const tokens = normalizeWhitespace(label).split(' ').filter(Boolean);
    if (tokens.length < 2 || tokens.length > 6) return;

    for (let index = 0; index < tokens.length - 1; index += 1) {
      const leftIsNumber = /^\d+$/.test(tokens[index]);
      const rightIsNumber = /^\d+$/.test(tokens[index + 1]);
      if (leftIsNumber === rightIsNumber) continue;

      const reordered = [...tokens];
      [reordered[index], reordered[index + 1]] = [reordered[index + 1], reordered[index]];
      const key = compact(reordered.join(' '));
      if (!reorderedAliasCandidates.has(key)) reorderedAliasCandidates.set(key, new Map());
      reorderedAliasCandidates.get(key).set(compact(label), label);
    }
  });
  const reorderedAliases = new Map(
    [...reorderedAliasCandidates.entries()]
      .filter(([, labels]) => labels.size === 1)
      .map(([key, labels]) => [key, [...labels.values()][0]])
  );

  const findExactAlias = (value) => aliases[value] || reorderedAliases.get(value);

  const editDistanceAtMostOne = (left, right) => {
    if (left === right) return 0;
    if (Math.abs(left.length - right.length) > 1) return 2;
    if (left.length === right.length) {
      const differences = [...left].map((letter, index) => letter === right[index] ? -1 : index).filter((index) => index >= 0);
      if (differences.length === 2 && differences[1] === differences[0] + 1
        && left[differences[0]] === right[differences[1]] && left[differences[1]] === right[differences[0]]) return 1;
    }

    let leftIndex = 0;
    let rightIndex = 0;
    let edits = 0;

    while (leftIndex < left.length && rightIndex < right.length) {
      if (left[leftIndex] === right[rightIndex]) {
        leftIndex += 1;
        rightIndex += 1;
        continue;
      }

      edits += 1;
      if (edits > 1) return 2;

      if (left.length > right.length) leftIndex += 1;
      else if (right.length > left.length) rightIndex += 1;
      else {
        leftIndex += 1;
        rightIndex += 1;
      }
    }

    if (leftIndex < left.length || rightIndex < right.length) edits += 1;
    return edits;
  };

  const uniqueFuzzyMatch = (value, candidates, getCandidate) => {
    let match = null;
    const valueDigits = digitSignature(value);
    for (const candidate of candidates) {
      const candidateValue = getCandidate(candidate);
      // A model number is identity, not spelling. Never turn 450 into 150,
      // HD5 into HD7, or otherwise fuzzy-correct numeric fitment data.
      if (digitSignature(candidateValue) !== valueDigits) continue;
      if (Math.abs(candidateValue.length - value.length) > 1) continue;
      if (editDistanceAtMostOne(value, candidateValue) !== 1) continue;
      if (match && getCandidate(match) !== candidateValue) return null;
      match = candidate;
    }
    return match;
  };

  const expandAlias = (token) => {
    const normalized = compact(token);
    const exactAlias = findExactAlias(normalized);
    if (exactAlias) return exactAlias;
    if (vocabulary.has(normalized)) return token;
    if (normalized.length < 7 || normalized.replace(/[^a-z]/g, '').length < 4) return token;

    const match = uniqueFuzzyMatch(normalized, aliasEntries, ([alias]) => alias);
    return match ? match[1] : token;
  };

  const correctSpelling = (token) => {
    const normalized = token.toLowerCase();
    if (normalized.length < 4 || /\d.*[a-z]/.test(normalized) || /^\d+$/.test(normalized) || vocabulary.has(normalized)) return token;

    const match = uniqueFuzzyMatch(normalized, data.vocabulary || [], (candidate) => candidate);
    return match || token;
  };

  const normalizeQuery = (query) => {
    const original = String(query || '').trim();
    if (!original) return '';
    if (cache.has(original)) return cache.get(original);
    if (skus.has(compact(original)) || original.length > 160) return original;

    const normalized = normalizeWhitespace(original);
    // Number words are converted only inside a recognized catalog phrase.
    const phraseQuery = resolvePhrases(normalized);
    const wholeAlias = resolvePhrase(phraseQuery) || findExactAlias(compact(phraseQuery));
    let result;

    if (wholeAlias) {
      result = wholeAlias;
    } else {
      let changed = phraseQuery !== normalized;
      const expandedTokens = phraseQuery.split(' ').map((token) => {
        const expanded = expandAlias(token);
        if (expanded !== token) changed = true;
        return expanded;
      });
      const correctedTokens = expandedTokens.join(' ').split(' ').map((token) => {
        const corrected = correctSpelling(token);
        if (corrected !== token) changed = true;
        return corrected;
      });
      result = changed ? resolvePhrases(correctedTokens.join(' ')) : original;
    }

    if (cache.size >= 250) cache.delete(cache.keys().next().value);
    cache.set(original, result);
    return result;
  };

  globalThis.RicochetSearchNormalizer = Object.freeze({
    normalizeQuery,
    stripModelYears,
    normalizeSearchQuery: (value) => normalizeQuery(stripModelYears(value)),
    normalizeProductQuery: (value) => {
      const original = String(value || '');
      if (!original || original.length > 160) return original;
      if (productCache.has(original)) return productCache.get(original);
      const normalized = normalizeWhitespace(original);
      const resolved = resolveProductTypes(normalized);
      const result = resolved === normalized ? original : resolved;
      if (productCache.size >= 250) productCache.delete(productCache.keys().next().value);
      productCache.set(original, result);
      return result;
    },
    source: data.source,
  });
})();
